/**
 * Marketing Tracking Service
 *
 * Owns the website-side wiring for Google Tag Manager, Google Ads, Google
 * Analytics, and (later) Meta. Centralizing this here keeps the rest of the
 * app free of inline gtag/dataLayer code and makes consent-aware tracking
 * testable and configurable per environment.
 *
 * Business rules:
 * - GTM HTML injection (`vite-plugin-gtm`) runs only for Vite `mode ===
 *   'production'` and a valid container ID, so e2e/dev builds never ship the
 *   snippet even if `.env.local` sets VITE_GTM_CONTAINER_ID.
 * - Runtime `dataLayer` helpers require `import.meta.env.MODE ===
 *   'production'` (or Vitest: see isGtmRuntimeAllowed) so the JS bundle does
 *   not fire marketing events in e2e or dev if the ID leaks via merged env
 *   files. Exception: Vite mode `e2e` with `VITE_E2E_MARKETING_DATALAYER`
 *   enables in-memory `window.dataLayer` pushes without loading gtm.js so
 *   Playwright can assert conversion contracts. For production builds,
 *   `plugins/vite-plugin-gtm.ts` injects the official GTM snippet plus
 *   Consent Mode v2 default-denied signals into index.html so tag verifiers
 *   see gtm.js in View Source.
 * - Google Consent Mode v2 is used: default consent is denied for every
 *   advertising/analytics signal, then updated when the user grants consent
 *   in the cookie preferences modal. This is required for compliant tag
 *   loading and lets Google model conversions for un-consented users.
 * - Conversions are deduped in-session by Stripe Checkout Session ID so the
 *   same subscription never fires twice (e.g. modal completion AND
 *   /account?subscription=success landing).
 * - Free-plan signups are deduped per user ID (Order ID for Google Ads).
 * - Email used for Google Ads Enhanced Conversions is hashed with SHA-256 in
 *   the browser before being pushed to the data layer; raw email never
 *   leaves the page.
 * - SPA page paths are sanitized before virtual_pageview pushes so GA4 never
 *   receives card tokens, record IDs, or query strings (see sanitizePagePath.ts).
 * - Funnel events (modal_open, modal_closed, modal_step_viewed, service_selected,
 *   service_scheduled, onboarding_*, contact_created) support drop-off analysis in GA4.
 */

import type { ConsentState } from '../types/consent';
import type { ServiceType } from '../types';
import {
  FREE_SIGNUP_CONVERSION_CURRENCY,
  getFreeSignupConversionValue,
} from '../config/marketingConversionConfig';
import type { OnboardingPlan, ServiceCatalogKind } from '../constants/marketingAnalyticsEvents';
import {
  MARKETING_EVENT_CAMPAIGN_CTA_CLICKED,
  MARKETING_EVENT_CAMPAIGN_ONBOARDING_VIEWED,
  MARKETING_EVENT_CAMPAIGN_PAGE_VIEWED,
  MARKETING_EVENT_CAMPAIGN_CHECKOUT_STARTED,
  MARKETING_EVENT_CONTACT_CREATED,
  MARKETING_EVENT_FREE_SIGNUP,
  MARKETING_EVENT_MARKETING_CONSENT_DENIED,
  MARKETING_EVENT_MARKETING_CONSENT_GRANTED,
  MARKETING_EVENT_MODAL_CLOSED,
  MARKETING_EVENT_MODAL_OPEN,
  MARKETING_EVENT_MODAL_STEP_VIEWED,
  MARKETING_EVENT_BEGIN_CHECKOUT,
  MARKETING_EVENT_ONBOARDING_COMPLETED,
  MARKETING_EVENT_ONBOARDING_STARTED,
  MARKETING_EVENT_ORDER_SCHEDULED,
  MARKETING_EVENT_SEASONAL_BANNER_CLICKED,
  MARKETING_EVENT_SEASONAL_BANNER_SHOWN,
  MARKETING_EVENT_SERVICE_SCHEDULED,
  MARKETING_EVENT_SERVICE_SELECTED,
  MARKETING_EVENT_SHOP_MENU_OPENED,
  MARKETING_EVENT_SHOP_PRODUCT_CLICKED,
  MARKETING_EVENT_SOCIAL_FOLLOW_CLICKED,
  MARKETING_EVENT_SIGNUP_CTA_CLICKED,
  MARKETING_EVENT_SUBSCRIPTION_PURCHASE,
  MARKETING_EVENT_TERMS_ACCEPTED,
  MARKETING_EVENT_VIEW_ITEM,
  MARKETING_EVENT_VIRTUAL_PAGEVIEW,
} from '../constants/marketingAnalyticsEvents';
import { clearCampaignSlug } from './campaignSlugService';
import type { SeasonalBannerSource } from '../constants/seasonalBanner';
import type { ShopNavProductKey } from '../constants/shopNav';
import type { SocialPlatform } from '../constants/socialProfiles';
import type { SignupCtaId } from '../constants/signupCtaIds';
import {
  clearModalAnalyticsSession,
  markModalAnalyticsCompletedForService,
  __resetModalAnalyticsSessionsForTests,
} from './modalAnalyticsSession';
import { sanitizePagePath, sanitizePathname } from './sanitizePagePath';
import {
  E2E_MARKETING_DATALAYER_STUB_CONTAINER_ID,
} from '../constants/marketingE2e';

/**
 * Google Consent Mode v2 signals.
 * Only the signals we actually use are typed here — see the GA docs for the
 * full list. Values are 'granted' or 'denied'.
 */
interface ConsentModeSignals {
  ad_storage: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
}

/**
 * Payload pushed to the data layer when a verified subscription is recorded.
 * Mirrors the contract documented in the Marketing Tags plan.
 */
export interface SubscriptionPurchasePayload {
  /** Stripe Checkout Session ID (cs_...). Used as transaction_id and event_id. */
  checkoutSessionId: string;
  /** Real billed amount in major currency units (e.g. 4.99). */
  value: number;
  /** ISO 4217 currency code, defaults to USD. */
  currency: string;
  /**
   * Subscription plan identifier from the backend, when known. Omitted on
   * the /account?subscription=success recovery path where the SubscriptionStatus
   * payload doesn't include the plan ID.
   */
  planId?: number;
  /** Subscription plan name (e.g. "Subscriber"). */
  planName: string;
  /** 'Monthly' | 'Annual' billing cadence. */
  billingInterval: string;
  /** Optional campaign slug (e.g. intro-annual) for attribution. */
  campaignSlug?: string;
  /**
   * SHA-256 hash of the normalized user email for Google Ads Enhanced
   * Conversions. Optional because users without consent or without an email
   * (mock mode, edge cases) should still be able to fire the event.
   */
  hashedEmail?: string;
}

/**
 * Payload pushed to the data layer when a user completes onboarding on the
 * free tier. `userId` is also sent as `user_id` for Google Ads Order ID dedupe.
 */
export interface FreeSignupPayload {
  /** Authenticated user ID from AuthContext. */
  userId: string;
  /**
   * SHA-256 hash of the normalized user email for Google Ads Enhanced
   * Conversions. Optional when marketing consent is denied or email missing.
   */
  hashedEmail?: string;
  /** First-touch UTMs for GA4 sign_up (PBI #1500). */
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/** Non-PII payload for service_scheduled funnel completion (all service types). */
export interface ServiceScheduledPayload {
  serviceType: ServiceType;
  eventTypeId: number;
  catalogItemKind: ServiceCatalogKind;
  catalogItemId: string;
  timeSlot?: string;
  isGuest?: boolean;
  /** True when a mailed-card order included a gift card (PBI #990). */
  hasGiftCard?: boolean;
  /** Gift-card face value in USD when selected; omit when none. */
  giftCardAmountUsd?: number | null;
}

/** Payload for modal_closed abandonment analytics. */
export interface ModalClosedPayload {
  modalId: string;
  lastStepNumber: number;
  lastStepName: string;
  completed: boolean;
}

/** GA4 ecommerce item payload for product-order funnel events (PBI #1204). */
export interface MarketingEcommerceItemPayload {
  /** Merchant Center offer id when known; otherwise catalog sku/slug/public id. */
  itemId: string;
  itemName: string;
  /** List price in major currency units (never margin). */
  price: number;
  currency?: string;
}

/** Server marketing snapshot on order create — public GMV fields only. */
export interface OrderScheduledMarketingPayload {
  transactionId: string;
  revenueUsd: number;
  itemPriceUsd: number;
  currency: string;
  merchantItemId?: string | null;
  itemName: string;
}

declare global {
  interface Window {
    /** Tag Manager data layer — seeded by vite-plugin-gtm in prod HTML when configured. */
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * True when Playwright e2e builds enable in-memory dataLayer pushes without
 * loading gtm.js (`VITE_E2E_MARKETING_DATALAYER=true` in mode `e2e`).
 */
function isE2eMarketingDataLayerStubEnabled(): boolean {
  if (import.meta.env.MODE !== 'e2e') return false;

  const flag = import.meta.env.VITE_E2E_MARKETING_DATALAYER;
  return flag === 'true' || flag === '1';
}

/**
 * Returns true when this bundle should execute marketing `dataLayer` helpers.
 * Production artifacts, Vitest (contract tests stub the env var), and the
 * e2e dataLayer stub. Dev mode stays off even if VITE_GTM_CONTAINER_ID leaks.
 */
function isGtmRuntimeAllowed(): boolean {
  if (import.meta.env.MODE === 'production') return true;

  // Vitest runs in Node; process.env.VITEST is set so unit tests can stub
  // VITE_GTM_CONTAINER_ID without shipping a production bundle.
  if (typeof process !== 'undefined' && process.env.VITEST === 'true') return true;

  if (isE2eMarketingDataLayerStubEnabled()) return true;

  return false;
}

/**
 * Resolve the configured GTM container ID. Returns undefined when the env
 * variable is empty or this bundle is not allowed to run marketing code.
 * E2E stub mode returns a sentinel id so existing gates pass without gtm.js.
 */
function getGtmContainerId(): string | undefined {
  if (!isGtmRuntimeAllowed()) return undefined;

  if (isE2eMarketingDataLayerStubEnabled()) {
    return E2E_MARKETING_DATALAYER_STUB_CONTAINER_ID;
  }

  const id = import.meta.env.VITE_GTM_CONTAINER_ID;
  if (typeof id !== 'string' || id.trim() === '') return undefined;
  return id.trim();
}

/**
 * In-session set of Stripe Checkout Session IDs we've already converted on.
 * Prevents double-counting when both the embedded checkout completion and
 * the /account?subscription=success landing trigger the same conversion.
 */
const dedupedSessionIds = new Set<string>();

/**
 * In-session set of user IDs we've already recorded a free signup for.
 * Prevents double-counting when subscription status is refreshed twice
 * during the onboarding-complete transition.
 */
const dedupedFreeSignupUserIds = new Set<string>();

/** Prevents duplicate onboarding_started per browser session. */
let onboardingStartedRecordedThisSession = false;

/** Prevents duplicate onboarding_completed per user per session. */
const dedupedOnboardingCompletedUserIds = new Set<string>();

/** Prevents duplicate service_scheduled per service + catalog + event type per session. */
const dedupedServiceScheduledKeys = new Set<string>();

/**
 * Set when a verified subscription_purchase conversion fires this session.
 * SubscriptionContext uses this to avoid mislabeling a paying user as free_signup
 * if status fields refresh in a non-atomic order.
 */
let subscriptionPurchaseRecordedThisSession = false;

/** Prevents duplicate campaign_checkout_started per slug per session. */
const dedupedCampaignCheckoutStartedSlugs = new Set<string>();

/** Prevents duplicate order_scheduled per transaction id per session. */
const dedupedOrderScheduledTransactionIds = new Set<string>();

/** Prevents duplicate begin_checkout per product item id per session. */
const dedupedBeginCheckoutItemIds = new Set<string>();

/** Prevents duplicate view_item per item id per page load session. */
const dedupedViewItemIds = new Set<string>();

const DEFAULT_ECOMMERCE_CURRENCY = 'USD';

const GOOGLE_BUSINESS_VERTICAL_RETAIL = 'retail';

/**
 * Clears any prior ecommerce object on the data layer, then pushes a GA4
 * ecommerce event. Required so item data from a prior event does not leak.
 */
function pushEcommerceEvent(
  eventName: string,
  currency: string,
  value: number,
  items: Array<Record<string, unknown>>,
): void {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: eventName,
    ecommerce: {
      currency,
      value,
      items,
    },
  });
}

/**
 * Builds a single-item GA4 ecommerce items array for remarketing tags.
 */
function buildRetailEcommerceItem(
  payload: MarketingEcommerceItemPayload,
): Record<string, unknown> {
  return {
    item_id: payload.itemId.trim(),
    item_name: payload.itemName.trim(),
    price: payload.price,
    quantity: 1,
    google_business_vertical: GOOGLE_BUSINESS_VERTICAL_RETAIL,
  };
}

/**
 * Returns true when a subscription_purchase conversion has fired this session.
 */
export function hasRecordedSubscriptionPurchaseThisSession(): boolean {
  return subscriptionPurchaseRecordedThisSession;
}

/**
 * Push a raw entry onto the data layer. Creates the array if missing.
 * Exported for tests and for the rare external caller that needs custom
 * events; prefer the typed helpers below for app code.
 */
export function pushDataLayer(entry: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(entry);
}

/**
 * Push a Google Consent Mode command (`default` / `update`) onto the data layer.
 *
 * Business rule (critical): Google's Consent Mode engine ONLY applies `consent`
 * commands that arrive on the data layer as a native `arguments` object — the
 * shape produced by the canonical `function gtag(){dataLayer.push(arguments)}`
 * pattern. A hand-built plain object like `{0:'consent',1:'update',2:{...}}`
 * is silently ignored by the consent engine, which previously left
 * `analytics_storage` and the three ad signals stuck at their default-denied
 * state even after the user clicked "Accept All". That suppressed GA4 to
 * cookieless pings (nothing in Realtime/DebugView) and forced Google Ads
 * conversions into consent-denied / modeled-only mode with Enhanced
 * Conversions disabled. We therefore must emit a real `arguments` object here.
 */
function pushConsentCommand(
  command: 'default' | 'update',
  signals: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer ?? [];
  const dataLayer = window.dataLayer;

  // Declared param-less so `arguments` is the untouched native Arguments
  // object (the exact structure Consent Mode requires); the cast lets us
  // invoke it with the gtag command tuple without TypeScript complaints.
  function gtag() {
    dataLayer.push(arguments as unknown as Record<string, unknown>);
  }

  (gtag as (...args: unknown[]) => void)('consent', command, signals);
}

/**
 * Map our internal cookie-consent state onto Google Consent Mode v2 signals.
 *
 * - Analytics cookies map to analytics_storage.
 * - Marketing cookies map to all three advertising signals because Google
 *   treats ad_storage, ad_user_data, and ad_personalization as one bucket
 *   from the user's perspective. Splitting them confuses the CMP UI without
 *   meaningfully changing the user's choice.
 */
export function consentStateToSignals(consent: ConsentState): ConsentModeSignals {
  return {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  };
}

/**
 * Legacy hook kept for backward compatibility. GTM bootstrap (Consent Mode v2
 * defaults + gtm.js) is injected into index.html at build time by
 * `vite-plugin-gtm` when `VITE_GTM_CONTAINER_ID` is set; the SPA no longer
 * injects scripts at runtime so Google Ads "Test connection" and tag diagnostics
 * see the container in View Source.
 */
export function initializeMarketingTracking(): void {
  // Intentionally empty — see file header and vite-plugin-gtm.
}

/**
 * Mimics the consent default + gtm.js pushes that `vite-plugin-gtm` injects
 * into production HTML. Vitest has no HTML transform output, so tests that
 * stub `VITE_GTM_CONTAINER_ID` call this after `__resetMarketingTrackingForTests`
 * to align `window.dataLayer` with a real browser load.
 */
export function __seedHtmlGtmBootstrapForTests(): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  window.dataLayer = window.dataLayer ?? [];

  // Mirror vite-plugin-gtm exactly: the default consent command is emitted as
  // a native arguments object via gtag() so tests exercise the real shape.
  pushConsentCommand('default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  pushDataLayer({ event: 'gtm.js', 'gtm.start': new Date().getTime() });
}

/**
 * True after this page load has already pushed `marketing_consent_granted`.
 * Resets when marketing is revoked so a later re-grant can load Meta / ChatGPT Ads again.
 */
let marketingConsentGrantedEventPushed = false;

/**
 * True while marketing cookies are currently granted this page load.
 * Used to emit `marketing_consent_denied` only on a real grant → revoke transition.
 */
let marketingConsentCurrentlyGranted = false;

/**
 * Push a Consent Mode v2 update reflecting the user's current cookie choices.
 *
 * Called by ConsentProvider whenever the user accepts, declines, or changes
 * preferences in the cookie banner / preferences modal. No-ops when GTM is
 * not configured.
 *
 * When marketing is granted, also pushes `marketing_consent_granted` once per
 * page load (after the Consent Mode update). Gallery Meta Pixel tags use GTM
 * "Require additional consent" and are not re-evaluated on Consent Update when
 * they were blocked on All Pages — so Meta / ChatGPT Ads base loads on this
 * event instead. When marketing is later revoked, pushes
 * `marketing_consent_denied` so OpenAI oaiq consent can flip to false.
 */
export function updateConsentSignals(consent: ConsentState): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  const signals = consentStateToSignals(consent);

  // Must be pushed as a native arguments object — see pushConsentCommand.
  // Cast: the concrete ConsentModeSignals interface has no index signature.
  pushConsentCommand('update', signals as unknown as Record<string, unknown>);

  if (!consent.marketing) {
    if (marketingConsentCurrentlyGranted) {
      pushDataLayer({ event: MARKETING_EVENT_MARKETING_CONSENT_DENIED });
    }

    marketingConsentCurrentlyGranted = false;
    marketingConsentGrantedEventPushed = false;
    return;
  }

  marketingConsentCurrentlyGranted = true;

  if (marketingConsentGrantedEventPushed) {
    return;
  }

  marketingConsentGrantedEventPushed = true;
  pushDataLayer({ event: MARKETING_EVENT_MARKETING_CONSENT_GRANTED });
}
/**
 * Push the user's Consent Mode v2 signals immediately before a conversion
 * event. React `useEffect` consent sync can run after SubscriptionContext
 * fires `free_signup` on the same mount (child effects run before parent
 * fetch, but stored-consent bootstrap and banner accept still race the
 * async onboarding-complete transition). Synchronous sync here ensures
 * Google Ads tags see `ad_storage: granted` when the user has opted in.
 *
 * No-ops when the user has not made a cookie choice yet (`isConsentSet`
 * false) so we never push a granted update for default-denied visitors.
 */
export function syncConsentBeforeConversion(
  consent: ConsentState,
  isConsentSet: boolean,
): void {
  if (!isConsentSet) return;
  updateConsentSignals(consent);
}

/**
 * Push a virtual page view for SPA route changes. GTM treats real document
 * loads as page views automatically, but React Router transitions need this
 * helper so GA4 sees each navigation.
 */
export function trackPageView(path: string): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  const { page_path, page_location } = sanitizePagePath(path);

  pushDataLayer({
    event: MARKETING_EVENT_VIRTUAL_PAGEVIEW,
    page_path,
    page_location,
  });
}

/**
 * Fires when any AppModal opens. Central instrumentation in AppModal uses the
 * required kebab-case `id` so every modal is a measurable funnel entry point.
 */
export function trackModalOpen(modalId: string): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  if (!modalId.trim()) return;

  pushDataLayer({
    event: MARKETING_EVENT_MODAL_OPEN,
    modal_id: modalId.trim(),
  });
}

/**
 * Fires when a multi-step wizard advances to a new step. Uses normalized
 * step_number/step_total for comparable GA4 funnels across entry contexts.
 */
export function trackModalStepViewed(
  modalId: string,
  stepIndex: number,
  stepName: string,
  stepNumber: number,
  stepTotal: number,
): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  if (!modalId.trim()) return;

  pushDataLayer({
    event: MARKETING_EVENT_MODAL_STEP_VIEWED,
    modal_id: modalId.trim(),
    step_index: stepIndex,
    step_name: stepName,
    step_number: stepNumber,
    step_total: stepTotal,
  });
}

/**
 * Fires when an AppModal closes. Carries the last step reached and whether
 * a completion event (e.g. service_scheduled) fired during this open cycle.
 */
export function trackModalClosed(payload: ModalClosedPayload): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  if (!payload.modalId.trim()) return;

  pushDataLayer({
    event: MARKETING_EVENT_MODAL_CLOSED,
    modal_id: payload.modalId.trim(),
    last_step_number: payload.lastStepNumber,
    last_step_name: payload.lastStepName,
    completed: payload.completed,
  });

  clearModalAnalyticsSession(payload.modalId);
}

/**
 * Fires once per session when the WelcomeOnboarding overlay first becomes
 * visible (signup funnel entry — not an AppModal).
 */
export function trackOnboardingStarted(): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (onboardingStartedRecordedThisSession) return false;

  onboardingStartedRecordedThisSession = true;
  pushDataLayer({ event: MARKETING_EVENT_ONBOARDING_STARTED });
  return true;
}

/**
 * Fires when onboarding completes. Free path pairs with free_signup; paid path
 * pairs with subscription_purchase on the same transition.
 */
export function trackOnboardingCompleted(
  userId: string,
  plan: OnboardingPlan = 'free',
): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (!userId) return false;
  if (dedupedOnboardingCompletedUserIds.has(userId)) return false;

  dedupedOnboardingCompletedUserIds.add(userId);
  pushDataLayer({
    event: MARKETING_EVENT_ONBOARDING_COMPLETED,
    plan,
  });
  return true;
}

/**
 * Fires when a free/organic surface sends the visitor to `/auth/signup`.
 *
 * Payload is non-PII: stable `cta_id` plus the sanitized current page path so
 * Explore funnels can attribute which landing CTA started signup.
 *
 * @param ctaId - Canonical id from SIGNUP_CTA_IDS
 */
export function trackSignupCtaClicked(ctaId: SignupCtaId): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SIGNUP_CTA_CLICKED,
    cta_id: ctaId,
    page_path: sanitizePathname(window.location.pathname),
  });
}

/**
 * Fires after `/account/accept-terms` succeeds (post-Entra, pre-app gate).
 *
 * @param termsVersion - Version string accepted (e.g. TERMS_VERSION)
 * @param isReacceptance - True when the user already had a previous version on file
 */
export function trackTermsAccepted(
  termsVersion: string,
  isReacceptance: boolean,
): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_TERMS_ACCEPTED,
    terms_version: termsVersion,
    is_reacceptance: isReacceptance,
  });
}

/**
 * Fires after a contact is successfully created via the API (all entry paths).
 */
export function trackContactCreated(): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({ event: MARKETING_EVENT_CONTACT_CREATED });
}

/**
 * Fires when the user picks a service at the smile-choice crossroads.
 */
export function trackServiceSelected(serviceType: ServiceType): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SERVICE_SELECTED,
    service_type: serviceType,
  });
}

/**
 * Fires when the public Shop menu opens (desktop Menu or mobile accordion).
 * Used to measure whether grouping products under Shop helps or hurts discovery.
 */
export function trackShopMenuOpened(surface: 'desktop' | 'mobile'): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SHOP_MENU_OPENED,
    surface,
  });
}

/**
 * Fires when a public Shop product link is clicked (nav or footer).
 */
export function trackShopProductClicked(
  productKey: ShopNavProductKey,
  serviceType: ServiceType,
  surface: 'nav' | 'footer',
): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SHOP_PRODUCT_CLICKED,
    product_key: productKey,
    service_type: serviceType,
    surface,
  });
}

/**
 * Fires when a public social follow link is clicked (footer → Meta profile).
 *
 * Call from Anchor onClick without preventDefault so native navigation still runs.
 *
 * @param platform Instagram or Facebook profile key.
 * @param surface Placement that hosted the link (footer today).
 */
export function trackSocialFollowClicked(
  platform: SocialPlatform,
  surface: 'footer',
): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SOCIAL_FOLLOW_CLICKED,
    platform,
    surface,
  });
}

/**
 * Fires once when the seasonal marketing banner is rendered for a visitor.
 *
 * @param source Holiday or evergreen banner origin.
 */
export function trackSeasonalBannerShown(source: SeasonalBannerSource): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SEASONAL_BANNER_SHOWN,
    source,
  });
}

/**
 * Fires when the visitor clicks the seasonal banner CTA area.
 *
 * @param source Holiday or evergreen banner origin.
 */
export function trackSeasonalBannerClicked(source: SeasonalBannerSource): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;

  pushDataLayer({
    event: MARKETING_EVENT_SEASONAL_BANNER_CLICKED,
    source,
  });
}

/**
 * Fires when a public product detail page loads (flowers or mailed cards).
 * Deduped in-session by item id.
 */
export function trackViewItem(payload: MarketingEcommerceItemPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (!payload.itemId.trim() || !payload.itemName.trim()) return false;

  const itemId = payload.itemId.trim();
  if (dedupedViewItemIds.has(itemId)) return false;

  dedupedViewItemIds.add(itemId);
  const currency = payload.currency?.trim() || DEFAULT_ECOMMERCE_CURRENCY;

  pushEcommerceEvent(
    MARKETING_EVENT_VIEW_ITEM,
    currency,
    payload.price,
    [buildRetailEcommerceItem(payload)],
  );
  return true;
}

/**
 * Fires when a flower or mailed-card order wizard reaches review with a product selected.
 * Not fired on modal open or edit mode. Deduped in-session by item id.
 */
export function trackBeginCheckout(payload: MarketingEcommerceItemPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (!payload.itemId.trim() || !payload.itemName.trim()) return false;

  const itemId = payload.itemId.trim();
  if (dedupedBeginCheckoutItemIds.has(itemId)) return false;

  dedupedBeginCheckoutItemIds.add(itemId);
  const currency = payload.currency?.trim() || DEFAULT_ECOMMERCE_CURRENCY;

  pushEcommerceEvent(
    MARKETING_EVENT_BEGIN_CHECKOUT,
    currency,
    payload.price,
    [buildRetailEcommerceItem(payload)],
  );
  return true;
}

/**
 * Fires after a flower or mailed-card pre-order is created. Uses the server
 * marketing snapshot (GMV + Merchant offer id). Never includes margin.
 * Deduped in-session by transaction id.
 */
export function trackOrderScheduled(payload: OrderScheduledMarketingPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (!payload.transactionId.trim() || !payload.itemName.trim()) return false;

  const transactionId = payload.transactionId.trim();
  if (dedupedOrderScheduledTransactionIds.has(transactionId)) return false;

  dedupedOrderScheduledTransactionIds.add(transactionId);
  const currency = payload.currency?.trim() || DEFAULT_ECOMMERCE_CURRENCY;
  const itemId = (payload.merchantItemId?.trim() || transactionId).trim();

  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: MARKETING_EVENT_ORDER_SCHEDULED,
    ecommerce: {
      transaction_id: transactionId,
      currency,
      value: payload.revenueUsd,
      items: [
        {
          item_id: itemId,
          item_name: payload.itemName.trim(),
          price: payload.itemPriceUsd,
          quantity: 1,
          google_business_vertical: GOOGLE_BUSINESS_VERTICAL_RETAIL,
        },
      ],
    },
  });
  return true;
}

/**
 * Fires after any schedulable service is successfully created via the API.
 * Deduped in-session by service_type + catalog_item_id + event_type_id.
 */
export function trackServiceScheduled(payload: ServiceScheduledPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;
  if (!payload.catalogItemId.trim()) return false;

  const dedupeKey = `${payload.serviceType}:${payload.catalogItemId}:${payload.eventTypeId}`;
  if (dedupedServiceScheduledKeys.has(dedupeKey)) return false;

  dedupedServiceScheduledKeys.add(dedupeKey);
  markModalAnalyticsCompletedForService(payload.serviceType);

  const entry: Record<string, unknown> = {
    event: MARKETING_EVENT_SERVICE_SCHEDULED,
    service_type: payload.serviceType,
    event_type_id: payload.eventTypeId,
    catalog_item_kind: payload.catalogItemKind,
    catalog_item_id: payload.catalogItemId.trim(),
    is_guest: payload.isGuest ?? false,
  };

  if (payload.timeSlot) {
    entry.time_slot = payload.timeSlot;
  }

  if (payload.hasGiftCard !== undefined) {
    entry.has_gift_card = payload.hasGiftCard;
  }

  if (payload.giftCardAmountUsd != null) {
    entry.gift_card_amount_usd = payload.giftCardAmountUsd;
  }

  pushDataLayer(entry);
  return true;
}

/**
 * Normalize and SHA-256-hash an email for Google Ads Enhanced Conversions.
 *
 * The hash is computed in the browser via SubtleCrypto. Raw email is never
 * pushed to the data layer or sent to Google. Returns undefined if the
 * Crypto API is unavailable (very old browsers / non-secure contexts) so
 * callers can fall through to a non-enhanced conversion.
 */
export async function hashEmailForEnhancedConversions(
  email: string,
): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;
  if (!email) return undefined;

  // Google's enhanced-conversions normalization spec: lowercase, trim, no
  // dot-removal for gmail (Google explicitly says do not strip dots).
  const normalized = email.trim().toLowerCase();
  if (!normalized) return undefined;

  const subtle = window.crypto?.subtle;
  if (!subtle || typeof TextEncoder === 'undefined') return undefined;

  try {
    const buffer = new TextEncoder().encode(normalized);
    const digest = await subtle.digest('SHA-256', buffer);

    // Convert ArrayBuffer to lowercase hex string (Google expects hex).
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (const byte of bytes) {
      hex += byte.toString(16).padStart(2, '0');
    }
    return hex;
  } catch {
    return undefined;
  }
}

/**
 * Fire the verified-subscription conversion event.
 *
 * Idempotent per Stripe Checkout Session ID: the second call with the same
 * checkoutSessionId is silently ignored, so the modal-completion handler
 * and the /account?subscription=success landing can both call this without
 * double-counting.
 *
 * Returns true when the event was actually pushed, false when it was
 * deduped or skipped because GTM is not configured.
 */
export function trackSubscriptionPurchase(payload: SubscriptionPurchasePayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;

  if (!payload.checkoutSessionId) return false;
  if (dedupedSessionIds.has(payload.checkoutSessionId)) return false;

  dedupedSessionIds.add(payload.checkoutSessionId);
  subscriptionPurchaseRecordedThisSession = true;

  pushDataLayer({
    event: MARKETING_EVENT_SUBSCRIPTION_PURCHASE,
    transaction_id: payload.checkoutSessionId,
    value: payload.value,
    currency: payload.currency,
    plan_id: payload.planId,
    plan_name: payload.planName,
    billing_interval: payload.billingInterval,
    campaign_slug: payload.campaignSlug,
    enhanced_conversion_data: payload.hashedEmail
      ? { sha256_email_address: payload.hashedEmail }
      : undefined,
  });

  if (payload.campaignSlug) {
    clearCampaignSlug();
  }

  return true;
}

/**
 * Campaign landing page viewed (e.g. /autopilot).
 */
export function trackCampaignPageViewed(campaignSlug: string): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  pushDataLayer({
    event: MARKETING_EVENT_CAMPAIGN_PAGE_VIEWED,
    campaign_slug: campaignSlug,
  });
}

/**
 * Primary CTA clicked on a paid campaign landing page.
 */
export function trackCampaignCtaClicked(campaignSlug: string): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  pushDataLayer({
    event: MARKETING_EVENT_CAMPAIGN_CTA_CLICKED,
    campaign_slug: campaignSlug,
  });
}

/**
 * Campaign-aware WelcomeOnboarding variant shown after signup.
 */
export function trackCampaignOnboardingViewed(campaignSlug: string): void {
  if (typeof window === 'undefined') return;
  if (!getGtmContainerId()) return;
  pushDataLayer({
    event: MARKETING_EVENT_CAMPAIGN_ONBOARDING_VIEWED,
    campaign_slug: campaignSlug,
  });
}

/**
 * Fire when Stripe Embedded Checkout is about to show for a paid campaign claim.
 *
 * Completes the funnel: campaign_page_viewed → campaign_cta_clicked →
 * campaign_checkout_started → subscription_purchase. Observation only —
 * never wire this as a Google Ads Primary conversion.
 *
 * Deduped per campaign_slug per browser session so auto-resume and plan
 * re-subscribe paths do not inflate funnel counts.
 */
export function trackCampaignCheckoutStarted(campaignSlug: string): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;

  const slug = campaignSlug.trim();
  if (!slug) return false;
  if (dedupedCampaignCheckoutStartedSlugs.has(slug)) return false;

  dedupedCampaignCheckoutStartedSlugs.add(slug);
  pushDataLayer({
    event: MARKETING_EVENT_CAMPAIGN_CHECKOUT_STARTED,
    campaign_slug: slug,
  });
  return true;
}

/**
 * Fire the free-plan signup conversion event.
 *
 * Idempotent per user ID: the second call with the same userId is silently
 * ignored. The user_id is consumed by GTM as the Google Ads Order ID for
 * server-side dedupe across sessions and devices.
 *
 * Returns true when the event was actually pushed, false when it was
 * deduped or skipped because GTM is not configured.
 */
export function trackFreeSignup(payload: FreeSignupPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!getGtmContainerId()) return false;

  if (!payload.userId) return false;
  if (dedupedFreeSignupUserIds.has(payload.userId)) return false;

  dedupedFreeSignupUserIds.add(payload.userId);

  pushDataLayer({
    event: MARKETING_EVENT_FREE_SIGNUP,
    user_id: payload.userId,
    value: getFreeSignupConversionValue(),
    currency: FREE_SIGNUP_CONVERSION_CURRENCY,
    enhanced_conversion_data: payload.hashedEmail
      ? { sha256_email_address: payload.hashedEmail }
      : undefined,
    ...(payload.utmSource ? { utm_source: payload.utmSource } : {}),
    ...(payload.utmMedium ? { utm_medium: payload.utmMedium } : {}),
    ...(payload.utmCampaign ? { utm_campaign: payload.utmCampaign } : {}),
  });

  return true;
}

/**
 * Returns true when GTM is enabled in the current build. Components can use
 * this to skip optional work (e.g. computing a hashed email) when no
 * downstream destination would consume it.
 */
export function isMarketingTrackingEnabled(): boolean {
  return getGtmContainerId() !== undefined;
}

/**
 * Reset all internal state. Test-only helper; not exported via the public
 * surface elsewhere.
 */
export function __resetMarketingTrackingForTests(): void {
  dedupedSessionIds.clear();
  dedupedFreeSignupUserIds.clear();
  dedupedOnboardingCompletedUserIds.clear();
  dedupedServiceScheduledKeys.clear();
  dedupedCampaignCheckoutStartedSlugs.clear();
  dedupedOrderScheduledTransactionIds.clear();
  dedupedBeginCheckoutItemIds.clear();
  dedupedViewItemIds.clear();
  onboardingStartedRecordedThisSession = false;
  subscriptionPurchaseRecordedThisSession = false;
  marketingConsentGrantedEventPushed = false;
  marketingConsentCurrentlyGranted = false;
  __resetModalAnalyticsSessionsForTests();
  if (typeof window !== 'undefined') {
    delete window.dataLayer;
  }
}
